import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog";
import { DEFAULT_IMAGE } from "@/lib/config/cdn";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Image as ImageIcon, X } from "lucide-react";
import { useState } from "react";
import PropertyImageFullscreenView from "./PropertyImageFullscreenView";

interface PropertyImageGridProps {
  images: string[];
  title: string;
  isFeatured?: boolean;
  listingCode?: string;
}

const PropertyImageGrid = ({
  images,
  title,
  isFeatured,
  listingCode,
}: PropertyImageGridProps) => {
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [initialSlide, setInitialSlide] = useState(0);

  // Use the provided images array or a placeholder if empty
  const imageUrls = images && images.length > 0 ? images : [DEFAULT_IMAGE];

  // Embla Carousel setup
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

  // Open fullscreen starting from the clicked image
  const openFullscreen = (index: number) => {
    setInitialSlide(index);
    setShowFullscreen(true);
  };

  return (
    <>
      {/* Desktop Grid View (lg and up) */}
      <div className="hidden lg:grid grid-cols-12 gap-2">
        {/* Main image (large) - takes 8 columns */}
        <div
          className="col-span-8 h-[400px] bg-gray-100 cursor-pointer relative overflow-hidden rounded-l-lg"
          onClick={() => openFullscreen(0)}
        >
          <img
            src={imageUrls[0]}
            alt={`${title} - Main Image`}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300 ease-in-out"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.onerror = null; // Prevent infinite loop
              target.src = DEFAULT_IMAGE;
            }}
          />

          {/* Overlays (Badge, Count, Code) */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent pointer-events-none"></div>
          {imageUrls.length > 1 && (
            <div className="absolute bottom-4 right-4 bg-black/70 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm flex items-center pointer-events-none">
              <ImageIcon className="w-4 h-4 mr-1" />
              <span>{imageUrls.length} Photos</span>
            </div>
          )}
          {isFeatured && (
            <div className="absolute top-4 left-4 bg-amber-500 text-white px-3 py-1 rounded-md flex items-center pointer-events-none">
              <span className="font-medium text-sm">Featured</span>
            </div>
          )}
          {listingCode && (
            <div className="absolute top-4 right-4 bg-gray-900/70 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs pointer-events-none">
              <span>#{listingCode}</span>
            </div>
          )}
        </div>

        {/* Smaller images (takes 4 columns) */}
        <div className="col-span-4 grid grid-rows-2 gap-2">
          {imageUrls.slice(1, 3).map((url, index) => (
            <div
              key={index + 1}
              className="h-[196px] bg-gray-100 cursor-pointer overflow-hidden relative"
              onClick={() => openFullscreen(index + 1)}
              style={{ borderRadius: index === 0 ? "0 8px 0 0" : "0 0 8px 0" }}
            >
              <img
                src={url}
                alt={`${title} - Image ${index + 2}`}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300 ease-in-out"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = DEFAULT_IMAGE;
                }}
              />
              {index === 1 && imageUrls.length > 3 && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white text-lg font-semibold pointer-events-none">
                  +{imageUrls.length - 3} more
                </div>
              )}
            </div>
          ))}
          {imageUrls.length === 2 && (
            <div className="h-[196px] bg-gray-100 rounded-br-lg"></div>
          )}
        </div>
      </div>

      {/* Mobile Carousel View (below lg) */}
      <div
        className="block lg:hidden overflow-hidden relative rounded-lg shadow-md"
        ref={emblaRef}
      >
        <div className="flex">
          {imageUrls.map((url, index) => (
            <div className="flex-grow-0 flex-shrink-0 w-full" key={index}>
              <div
                className="h-[300px] bg-gray-100 cursor-pointer relative"
                onClick={() => openFullscreen(index)}
              >
                <img
                  src={url}
                  alt={`${title} - Image ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.src = DEFAULT_IMAGE;
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent pointer-events-none"></div>
                {imageUrls.length > 1 && (
                  <div className="absolute bottom-4 right-4 bg-black/70 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm flex items-center pointer-events-none">
                    <ImageIcon className="w-4 h-4 mr-1" />
                    <span>
                      {index + 1} / {imageUrls.length}
                    </span>
                  </div>
                )}
                {isFeatured && (
                  <div className="absolute top-4 left-4 bg-amber-500 text-white px-3 py-1 rounded-md flex items-center pointer-events-none">
                    <span className="font-medium text-sm">Featured</span>
                  </div>
                )}
                {listingCode && (
                  <div className="absolute top-4 right-4 bg-gray-900/70 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs pointer-events-none">
                    <span>#{listingCode}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        {emblaApi && imageUrls.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white hover:bg-black/70 hover:text-white rounded-full h-8 w-8"
              onClick={() => emblaApi.scrollPrev()}
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white hover:bg-black/70 hover:text-white rounded-full h-8 w-8"
              onClick={() => emblaApi.scrollNext()}
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </>
        )}
      </div>

      {/* Fullscreen Dialog */}
      <Dialog open={showFullscreen} onOpenChange={setShowFullscreen}>
        <DialogContent className="max-w-none w-full h-full p-0 bg-black/90 border-none flex items-center justify-center">
          <PropertyImageFullscreenView
            images={imageUrls}
            title={title}
            initialSlide={initialSlide}
            onClose={() => setShowFullscreen(false)}
          />
          <DialogClose asChild className="absolute top-4 right-4 z-50">
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20 hover:text-white rounded-full"
            >
              <X className="h-6 w-6" />
            </Button>
          </DialogClose>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PropertyImageGrid;
